import React, { useState } from 'react';

interface Task {
  id: string;
  number: number;
  title: string;
  description: string;
  requirements: Array<{
    text: string;
    subitems?: string[];
  }>;
  verification: string[];
}

const tasks: Task[] = [
  {
    id: 'task-1',
    number: 1,
    title: 'Diagnose Memory Issues',
    description: 'Analyze the system to identify all memory-related problems including swap, OOM killer behavior, and memory allocation issues.',
    requirements: [
      {
        text: 'Check current memory usage and identify memory pressure indicators'
      },
      {
        text: 'Verify swap space status and configuration'
      },
      {
        text: 'Review OOM killer activity in system logs'
      },
      {
        text: 'Identify processes with high memory consumption or memory leaks'
      },
      {
        text: 'Analyze virtual memory parameters and their current settings'
      },
      {
        text: 'Check NUMA memory allocation if applicable'
      }
    ],
    verification: [
      'You have identified the swap space status',
      'You know which processes are consuming excessive memory',
      'You have reviewed OOM killer logs and patterns',
      'You understand the current memory overcommit settings',
      'You have documented all memory-related issues to fix'
    ]
  },
  {
    id: 'task-2',
    number: 2,
    title: 'Configure Swap Space',
    description: 'Set up and activate swap space to provide virtual memory overflow capacity and prevent OOM conditions.',
    requirements: [
      {
        text: 'Create a 2GB swap file at /swapfile'
      },
      {
        text: 'Set /swapfile permissions to 600'
      },
      {
        text: 'Format /swapfile as swap space'
      },
      {
        text: 'Activate the swap space'
      },
      {
        text: 'Configure swap to persist across reboots in /etc/fstab'
      },
      {
        text: 'Configure vm.swappiness to a value from 10 through 60 and persist it'
      }
    ],
    verification: [
      'Swap space is active and showing in free command',
      'Swap space is at least 2GB',
      'Swap file has correct permissions (600)',
      '/swapfile is configured in /etc/fstab for persistence',
      'Swappiness is set to an appropriate value',
      'System shows increased available virtual memory'
    ]
  },
  {
    id: 'task-3',
    number: 3,
    title: 'Fix Memory Overcommit Settings',
    description: 'Adjust memory overcommit parameters to prevent aggressive memory allocation that leads to OOM kills.',
    requirements: [
      {
        text: 'Review current vm.overcommit_memory setting'
      },
      {
        text: 'Set appropriate overcommit policy:',
        subitems: ['Use vm.overcommit_memory = 0 for heuristic overcommit']
      },
      {
        text: 'Apply settings using sysctl'
      },
      {
        text: 'Make settings persistent in /etc/sysctl.conf'
      },
      {
        text: 'Verify new settings are active'
      }
    ],
    verification: [
      'Memory overcommit settings are appropriate for workload',
      'Settings are persisted in sysctl configuration',
      'No excessive memory allocation errors',
      'System allows reasonable memory requests',
      'Critical processes are not denied memory'
    ]
  },
  {
    id: 'task-4',
    number: 4,
    title: 'Control OOM Killer Behavior',
    description: 'Configure OOM killer to protect critical processes and handle memory exhaustion more intelligently.',
    requirements: [
      {
        text: 'Identify critical system processes that should be protected'
      },
      {
        text: 'Set a negative oom_score_adj value for the active SSH daemon process'
      },
      {
        text: 'Confirm pgrep -o sshd points to a process with a negative oom_score_adj'
      }
    ],
    verification: [
      'The active SSH daemon has a negative oom_score_adj value',
      'OOM killer targets expendable processes first',
      'System services remain stable under memory pressure',
      'Recovery from OOM conditions is predictable'
    ]
  },
  {
    id: 'task-5',
    number: 5,
    title: 'Stop Memory Leaks',
    description: 'Identify and stop processes with memory leaks that are consuming excessive system memory.',
    requirements: [
      {
        text: 'Use top or htop to identify processes with growing memory usage'
      },
      {
        text: 'Check for processes with high RSS (Resident Set Size)'
      },
      {
        text: 'Review process memory maps in /proc/[pid]/maps'
      },
      {
        text: 'Stop memory-leak.service'
      },
      {
        text: 'Disable memory-leak.service so it does not restart on boot'
      },
      {
        text: 'Stop all stress-ng memory workload processes'
      }
    ],
    verification: [
      'No processes showing continuous memory growth',
      'memory-leak.service is inactive or failed',
      'memory-leak.service is disabled',
      'stress-ng is not running',
      'System memory usage remains within acceptable bounds',
      'No unexpected memory exhaustion over time'
    ]
  },
  {
    id: 'task-6',
    number: 6,
    title: 'Optimize Virtual Memory Settings',
    description: 'Fine-tune kernel virtual memory parameters for optimal performance and stability.',
    requirements: [
      {
        text: 'Configure vm.dirty_ratio to a value from 5 through 30'
      },
      {
        text: 'Configure vm.dirty_background_ratio to a value from 5 through 15'
      },
      {
        text: 'Adjust vm.vfs_cache_pressure to a value from 50 through 150'
      },
      {
        text: 'Set vm.min_free_kbytes to at least 4096'
      },
      {
        text: 'Apply all virtual memory settings live and persist them in sysctl configuration'
      }
    ],
    verification: [
      'Virtual memory parameters are optimized for workload',
      'Cache and buffer usage is balanced',
      'System maintains adequate free memory',
      'Dirty page ratios are in the required ranges',
      'Virtual memory settings persist through sysctl configuration',
      'No excessive page faults or swapping',
      'Memory performance metrics show improvement'
    ]
  }
];

export function MemoryManagementMayhemInstructions() {
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [completedChecks, setCompletedChecks] = useState<Set<string>>(new Set());
  const [completedTasks, setCompletedTasks] = useState<Set<number>>(new Set());

  const handleTaskClick = (index: number) => {
    setCurrentTaskIndex(index);
  };

  const handleNextTask = () => {
    if (currentTaskIndex < tasks.length - 1) {
      setCurrentTaskIndex(currentTaskIndex + 1);
    } else {
      setCurrentTaskIndex(0);
    }
  };

  const handlePreviousTask = () => {
    if (currentTaskIndex > 0) {
      setCurrentTaskIndex(currentTaskIndex - 1);
    }
  };

  const toggleVerification = (taskId: string, checkIndex: number) => {
    const checkId = `${taskId}-check-${checkIndex}`;
    const newChecks = new Set(completedChecks);
    
    if (newChecks.has(checkId)) {
      newChecks.delete(checkId);
    } else {
      newChecks.add(checkId);
    }
    
    setCompletedChecks(newChecks);

    // Check if all verifications for this task are completed
    const currentTask = tasks[currentTaskIndex];
    const allChecksCompleted = currentTask.verification.every(
      (_, idx) => newChecks.has(`${currentTask.id}-check-${idx}`)
    );

    if (allChecksCompleted && !completedTasks.has(currentTaskIndex)) {
      const newCompletedTasks = new Set(completedTasks);
      newCompletedTasks.add(currentTaskIndex);
      setCompletedTasks(newCompletedTasks);
    }
  };

  const currentTask = tasks[currentTaskIndex];
  const progressPercentage = ((currentTaskIndex + 1) / tasks.length) * 100;

  return (
    <div className="bg-[#232937] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#10b981] to-[#059669] px-8 py-6">
        <h2 className="text-2xl font-semibold text-[#1a1f2e]">Instructions</h2>
      </div>

      {/* Content Area - Increased to 1800px for less scrolling */}
      <div className="flex h-[1800px]">
        {/* Sidebar Navigation */}
        <div className="w-[280px] bg-[#1a1f2e] border-r border-[#2d3748] overflow-y-auto">
          {tasks.map((task, index) => (
            <div
              key={task.id}
              onClick={() => handleTaskClick(index)}
              className={`flex items-center px-5 py-4 cursor-pointer border-b border-[#2d3748] hover:bg-[#2d3748] transition-all relative ${
                index === currentTaskIndex ? 'bg-[#2d3748] border-l-[3px] border-l-[#10b981] pl-[17px]' : ''
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mr-3 flex-shrink-0 ${
                completedTasks.has(index) ? 'bg-[#059669]' : 'bg-[#10b981]'
              } text-[#1a1f2e]`}>
                {task.number}
              </div>
              <div className={`text-sm ${index === currentTaskIndex ? 'text-[#e2e8f0] font-medium' : 'text-[#cbd5e1]'}`}>
                {task.title}
              </div>
              {completedTasks.has(index) && (
                <span className="absolute right-5 text-[#10b981] font-bold text-sm">✓</span>
              )}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8 bg-[#1e293b] overflow-y-auto">
          <h3 className="text-[22px] font-semibold text-[#e2e8f0] mb-4">
            Task {currentTask.number}: {currentTask.title}
          </h3>
          <p className="text-[#94a3b8] mb-8 leading-[1.6] text-[15px]">
            {currentTask.description}
          </p>

          {/* Requirements Section */}
          <div className="mb-8">
            <h4 className="text-[#10b981] text-xs uppercase tracking-[1px] mb-4 font-semibold flex items-center">
              <span className="w-5 h-[2px] bg-[#10b981] mr-[10px]"></span>
              What You Need To Do
            </h4>
            
            {currentTask.requirements.map((requirement, reqIndex) => (
              <div key={reqIndex} className="bg-[#232937] border border-[#2d3748] rounded-lg p-4 mb-3 hover:border-[#374151] hover:shadow-md transition-all">
                <div className="text-[#e2e8f0] text-sm leading-[1.8]">
                  <span className="font-medium">{reqIndex + 1}. {requirement.text}</span>
                  {requirement.subitems && (
                    <ul className="mt-2 ml-5 space-y-1">
                      {requirement.subitems.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#10b981] mr-2 mt-0.5">•</span>
                          <span className="text-[#cbd5e1]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Verification Section */}
          <div className="bg-[#232937] border-2 border-dashed border-[#475569] rounded-lg p-5 mt-6">
            <div className="flex items-center text-[#10b981] font-semibold mb-4 text-base">
              <span className="w-5 h-5 bg-[#10b981] text-[#1a1f2e] rounded-full flex items-center justify-center text-xs mr-[10px]">
                ✓
              </span>
              Verification
            </div>
            
            {currentTask.verification.map((check, checkIndex) => (
              <div
                key={checkIndex}
                onClick={() => toggleVerification(currentTask.id, checkIndex)}
                className="flex items-center p-[10px_12px] bg-[#1a1f2e] rounded-md mb-2 cursor-pointer hover:bg-[#1e293b] hover:translate-x-[3px] transition-all last:mb-0"
              >
                <div className={`w-[18px] h-[18px] border-2 border-[#10b981] rounded flex items-center justify-center mr-3 flex-shrink-0 transition-all ${
                  completedChecks.has(`${currentTask.id}-check-${checkIndex}`) ? 'bg-[#10b981]' : ''
                }`}>
                  {completedChecks.has(`${currentTask.id}-check-${checkIndex}`) && (
                    <span className="text-[#1a1f2e] text-xs font-bold">✓</span>
                  )}
                </div>
                <span className="text-[#cbd5e1] text-sm">{check}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Progress Footer */}
      <div className="flex items-center justify-between px-6 py-4 bg-[#1a1f2e] border-t border-[#2d3748]">
        <div className="text-[13px] text-[#94a3b8]">
          Progress: Task {currentTaskIndex + 1} of {tasks.length}
        </div>
        
        <div className="w-[200px] h-[6px] bg-[#2d3748] rounded-[3px] overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#10b981] to-[#34d399] transition-all duration-500 rounded-[3px]"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        <div className="flex gap-[10px]">
          <button
            onClick={handlePreviousTask}
            disabled={currentTaskIndex === 0}
            className="px-4 py-2 bg-[#2d3748] border border-[#374151] text-[#cbd5e1] rounded-md text-[13px] cursor-pointer transition-all hover:bg-[#374151] hover:text-[#e2e8f0] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={handleNextTask}
            className="px-5 py-2 bg-gradient-to-r from-[#10b981] to-[#059669] text-[#1a1f2e] rounded-md text-[13px] font-semibold cursor-pointer transition-all hover:shadow-lg hover:scale-105"
          >
            Next Task
          </button>
        </div>
      </div>
    </div>
  );
}

export default MemoryManagementMayhemInstructions;
